import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  Skeleton,
  Stack
} from '@chakra-ui/react'
import { CheckboxFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsVehiclePagedQuery, OfferType } from 'BrokerTsLib/src/dtos'
import type { InsuranceParamsType } from 'BrokerTsLib/src/enums'
import { PolicyStatusType, PolicyStatusTypes, Product, useInsuranceParamsTypeName } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useContext, useEffect, useState } from 'react'

import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { useGetInsuranceParamsTypes } from '@/hooks/useInsuranceParamsTypes'
import { shortInsurancesCarsContext } from '@/pages/vehicle/shortInsurances'

import { useGetOfferTypes } from '@/hooks/useOfferTypes'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { transformVIN } from 'BrokerTsLib/src/Utils/vin'
import { InputGroup } from '../../components/InputGroup'
import { useAuth } from '../../context/AuthContext'
import { usePartner } from '../../context/PartnerContext'
import { useInsurers } from '../../hooks'
import { Search } from '../../images/Search'
import { conceptsCarsContext } from '../../pages/vehicle/concepts'
import { contractsCarsContext } from '../../pages/vehicle/contracts'
import { SearchWrapper } from './SearchWrapper'
import { OrganizationUserFormControls } from './shared/OrganizationUserFormControls'

interface CarSearchFormProps {
  onSubmit: (search: InsuranceParamsVehiclePagedQuery) => void
  filter: InsuranceParamsVehiclePagedQuery
  listType: 'contracted' | 'opened'
}

export const CarSearchForm = ({
  onSubmit,
  filter,
  listType,
}: CarSearchFormProps) => {
  const { app, product } = useApp()

  const { translate } = useTranslationsContext()

  let ctx
  if (app == Apps.shortInsurance) ctx = shortInsurancesCarsContext
  else if (listType == 'contracted') ctx = contractsCarsContext
  else ctx = conceptsCarsContext

  const { loading: loadingList } = useContext(ctx)

  const methods = useForm<InsuranceParamsVehiclePagedQuery>({
    values: filter,
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods

  // vždy načítat seznam platných pojišťoven - JAP reklamuje, že se mu zobrazují pojišťovny, se kterými nespolupracuje
  const { data: allInsurers, isFetching: isFetchingInsurers } = useInsurers(
    null,
    false,
    { enabled: app != Apps.shortInsurance },
  )

  const { user } = useAuth()
  const { currentPartner } = usePartner()

  const insuranceParamsTypes = useGetInsuranceParamsTypes()
  const offerTypes = useGetOfferTypes()

  const getInsuranceParamsTypeName = useInsuranceParamsTypeName()
  const insuranceTypes: {
    name: string
    offerType: OfferType | null
    insuranceParamsType: InsuranceParamsType | null
  }[] = [
      ...insuranceParamsTypes.map(x => {
        return {
          name: getInsuranceParamsTypeName(x) ?? '',
          offerType: null,
          insuranceParamsType: x
        }
      }),
      ...offerTypes.map(x => {
        return {
          name: x.name,
          offerType: x.offerType,
          insuranceParamsType: null
        }
      })
    ]

  const [setselectedTypPojisteni, setSetselectedTypPojisteni] = useState<number | null>(null)
  const selectedInsuranceParamsType = watch('insuranceParamsType')
  const selectedOfferType = watch('offerType')
  useEffect(() => {
    if (selectedInsuranceParamsType)
      setSetselectedTypPojisteni(insuranceTypes.findIndex((x) => x.insuranceParamsType == selectedInsuranceParamsType))
    else if (selectedOfferType)
      setSetselectedTypPojisteni(insuranceTypes.findIndex((x) => x.offerType == selectedOfferType))
    else
      setSetselectedTypPojisteni(null)
  }, [selectedInsuranceParamsType, selectedOfferType])

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit((filter) => {
          return onSubmit({ ...filter, pageIndex: 1 })
        })}
      >
        <SearchWrapper>
          {app != Apps.shortInsurance && (
            <FormControl variant="fullWidth">
              <FormLabel htmlFor="offerNumber">{translate('insuranceParamsId', 'Číslo nabídky')}</FormLabel>
              <InputGroup
                id="offerNumber"
                placeholder="Např: 73622"
                {...register('insuranceParamsId')}
              />
            </FormControl>
          )}

          {app == Apps.shortInsurance && (
            <>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateFrom">Počátek pojištění od</FormLabel>
                <Datepicker name="insuranceStartDate" />
              </FormControl>

              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateTo">Konec pojištění do</FormLabel>
                <Datepicker name="insuranceEndDate" />
              </FormControl>

              <FormControl variant="fullWidth">
                <FormLabel htmlFor="listType">Stav smlouvy</FormLabel>
                <Select {...register('listType')}>
                  <option value="">Všechny</option>
                  <option value="1">Ukončené</option>
                  <option value="2">Aktivní</option>
                </Select>
              </FormControl>
            </>
          )}

          {listType == 'contracted' && (
            <>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="contractNumber">{translate('contractNumber', 'Číslo PS')}</FormLabel>
                <InputGroup
                  id="contractNumber"
                  placeholder="Např: 73622"
                  {...register('contractNumber')}
                />
              </FormControl>

              {product == Product.VehicleLeasingInsurance && (
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="leasingContractNumber">{translate('leasingContractNumber', 'Číslo LS')}</FormLabel>
                  <InputGroup
                    id="leasingContractNumber"
                    placeholder="Např: LS123456"
                    {...register('leasingContractNumber')}
                  />
                </FormControl>
              )}

              {app != Apps.shortInsurance && (
                <>
                  <FormControl variant="fullWidth">
                    <FormLabel htmlFor="dateFrom">{translate('dateContractedFrom', 'Datum sjednání od')}</FormLabel>
                    <Datepicker name="dateContractedFrom" />
                  </FormControl>

                  <FormControl variant="fullWidth">
                    <FormLabel htmlFor="dateTo">{translate('dateContractedTo', 'Datum sjednání do')}</FormLabel>
                    <Datepicker name="dateContractedTo" />
                  </FormControl>

                  <FormControl variant="fullWidth">
                    <FormLabel>{translate('anniversaryFrom', 'Datum výročí od')}</FormLabel>
                    <Datepicker name="anniversaryFrom" />
                  </FormControl>

                  <FormControl variant="fullWidth">
                    <FormLabel>{translate('anniversaryTo', 'Datum výročí do')}</FormLabel>
                    <Datepicker name="anniversaryTo" />
                  </FormControl>
                </>
              )}
            </>
          )}

          {listType == 'opened' && (
            <>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateFrom">{translate('dateFrom', 'Datum od')}</FormLabel>
                <Datepicker name="dateCreatedFrom" />
              </FormControl>

              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateTo">{translate('dateTo', 'Datum do')}</FormLabel>
                <Datepicker name="dateCreatedTo" />
              </FormControl>
            </>
          )}

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="insuranceCompany">{translate('insurer', 'Pojišťovna')}</FormLabel>

            <Skeleton isLoaded={!isFetchingInsurers}>
              <Select {...register('insurerId')}>
                <option value="">Vyberte</option>
                {allInsurers?.data.map((company) => {
                  return (
                    <option key={company.id} value={company.id.toString()}>
                      {company.description}
                    </option>
                  )
                })}
              </Select>
            </Skeleton>
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="spz">{translate('registrationMark', 'Registrační značka')}</FormLabel>

            <InputGroup
              id="spz"
              placeholder="Např: 1A1 0000"
              {...register('registrationMark')}
            />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="vin">VIN</FormLabel>

            <InputGroup
              id="vin"
              placeholder="Např: 2HNYD188068515943"

              {...register('vin', {
                onChange: (e) => { setValue('vin', transformVIN(e.target.value) ?? undefined) }
              })}
            />
          </FormControl>

          {app != Apps.shortInsurance && (
            <>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="insureeName">
                  {product == Product.VehicleLeasingInsurance ? translate('client', 'Klient') : translate('policyHolder', 'Pojistník')} ({translate('surnameOrCompany', 'příjmení/název firmy')})
                </FormLabel>

                <InputGroup
                  id="insureeName"
                  placeholder="Např: Novák"
                  {...register('legalPersonName')}
                />
              </FormControl>

              <FormControl variant="fullWidth">
                <FormLabel htmlFor="insureeNumber">
                  {product == Product.VehicleLeasingInsurance ? translate('client', 'Klient') : translate('policyHolder', 'Pojistník')} ({translate('birthNumberOrCompanyId', 'rodné číslo/IČO')})
                </FormLabel>

                <InputGroup
                  id="insureeNumber"
                  placeholder="Např: 9001012233"
                  {...register('legalPersonIdentification')}
                />
              </FormControl>
            </>
          )}

          <OrganizationUserFormControls />

          {user?.isSupervisor &&
            currentPartner &&
            currentPartner.partner! == 'Kapitol' && (
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="userCity">{translate('userCity', 'Obec uživatele')}</FormLabel>

                  <InputGroup id="userCity" {...register('userCity')} />
                </FormControl>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="userStreet">{translate('userStreet', 'Ulice uživatele')}</FormLabel>

                  <InputGroup id="userStreet" {...register('userStreet')} />
                </FormControl>
              </>
            )}

          {insuranceTypes.length > 1 && app !== Apps.shortInsurance &&
            <FormControl>
              <FormLabel>{translate('insuranceType', 'Typ pojištění')}</FormLabel>

              <Select
                value={setselectedTypPojisteni?.toString() ?? ''}
                onChange={(e) => {
                  const numberValue = Number(e.target.value)
                  if (isNaN(numberValue)) {
                    setValue('insuranceParamsType', null)
                    setValue('offerType', null)
                    return
                  }

                  const item = insuranceTypes[numberValue]
                  setValue('insuranceParamsType', item?.insuranceParamsType)
                  setValue('offerType', item?.offerType)
                }}

              >
                <option value="">{translate('selectInsuranceType', 'Vyberte typ pojištění')}</option>
                {insuranceTypes?.map((type, i) => (
                  <option
                    key={i}
                    value={i.toString()}
                  >
                    {type.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          }

          {listType == 'contracted' && app !== Apps.shortInsurance && (
            <SelectFormControl label='Stav smlouvy' w='full' control={methods.control} name="contractStateId" valueAsNumber
              options={[
                { value: 0, label: translate('contractState.all', 'Všechny smlouvy') },
                { value: 1, label: translate('contractState.active', 'Pouze aktivní') },
                { value: 2, label: translate('contractState.cancelled', 'Pouze stornované') },
              ]}
            />
          )}

          {listType == 'contracted' && app !== Apps.shortInsurance && currentPartner?.partner == 'VWFS' && (
            <SelectFormControl label='Status smlouvy' w='full' control={methods.control} name="policyStatus" valueAsNumber
              unselectedText={translate('policyStatus.all', 'Všechny')}
              options={PolicyStatusTypes
                .filter(([value]) => ![
                  PolicyStatusType.Active,
                  PolicyStatusType.Updated,
                  PolicyStatusType.Cancelled,
                  PolicyStatusType.Created,
                  PolicyStatusType.NotCreated,
                ].includes(value as any))
                .map(([value, label]) => ({ value, label }))}
            />
          )}


          <Stack justifyContent='center' gap={3}>
            {user?.poleas?.poleasEnabled && app !== Apps.shortInsurance && (
              <CheckboxFormControl name='showPoleas' control={methods.control} label={translate('searchForm.includePoleas', 'Zahrnout nabídky z Poleas')} />
            )}
            {user?.adminCebiaRights &&
              <CheckboxFormControl control={methods.control} name={'showDeleted'} label={translate('includeDeleted', 'Zahrnout smazané')} />
            }
          </Stack>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gridColumn: { base: '', md: '1 / 5' },
            }}
          >
            <Button
              type="submit"
              colorScheme="primary"
              isLoading={isSubmitting || loadingList}
            >
              <Search />
              {translate('search', 'Vyhledat')}
            </Button>
          </Box>
        </SearchWrapper>
      </form>
    </FormProvider>
  )
}
